import React from 'react'
import PropTypes from 'prop-types'
import {
  Page, View, Document, StyleSheet, Font
} from '@react-pdf/renderer'

import colors from '@settings/styleSettings'

import InterRegular from '@assets/fonts/Inter-Regular.ttf'
import InterBold from '@assets/fonts/Inter-Bold.ttf'
import { getOnlyDateFromFormattedDateStr, isValidDate } from '@util/timeUtil'
import { MainTitle } from './pdfComponents/MainTitle'
import { Line } from './pdfComponents/Line'
import { Signature } from './pdfComponents/Signature'
import Text from './pdfComponents/Text'

import InsurerEnum from '@util/InsurerEnum'
import DoubleImage from './pdfComponents/DoubleImages'
import DoubleTextWithCard from './pdfComponents/DoubleTextWithCard'

Font.register({ family: 'Inter-Regular', src: InterRegular })
Font.register({ family: 'Inter-Bold', src: InterBold })

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#FFFFFF',
  },
  section: {
    margin: '24 0',
  },
  container: {
    margin: '0 24',
    width: '100%',
  },
  firstPart: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  strong: {
    color: colors.strongGray,
    fontSize: 12,
    fontFamily: 'Inter-Bold',
  },
  text: {
    fontSize: 12,
  },
  signature: {
    color: colors.strongGray,
    fontSize: 12,
    textAlign: 'right',
  },
})

// Create Document Component
const LoanInformedConsent = ({ termData }) => {
  const insurer = InsurerEnum['unimed-assis']
  const
    {
      beneficiaryName,
      cpf, generalImage, identificationImage, loanDate, loanLimitDate, assign, equipmentName

    } = termData || {}
  const formattedDate = getOnlyDateFromFormattedDateStr(loanDate)
  const formattedLimitDate = getOnlyDateFromFormattedDateStr(loanLimitDate)

  const formattedCpf = cpf?.replace(
    /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
    '$1.$2.$3-$4'
  )
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <MainTitle
            text="Termo de empréstimo"
            topTitle={beneficiaryName}
            bottomTitle={`Termo Assinado em: ${formattedDate}`}
            insurer={insurer}
            topTitleColor={colors.strongGray}
            bottomTitleColor={colors.strongGray}
          />
          <Line lineColor={colors.softGray} />
          <View style={{ marginTop: 20 }}>
            <Text style={[{ color: colors.strongGray }, styles.text]}>
              Eu

              <Text style={styles.strong}>
                {' '}
                {beneficiaryName}
              </Text>
              , portador(a) do CPF n°
              <Text style={styles.strong}>
                {' '}
                {formattedCpf}
              </Text>
              , declaro que recebi e estou ciente das orientações abaixo.
              Desse modo, manifesto ainda para fins de responsabilidade,
              que recebi nesta data o equipamento citado abaixo em perfeitas condições,
              para fins de execução das atividades inerentes à organização,
              devendo zelar pela sua guarda,
              conservação e, se necessário, devolução ao final do contrato.
              Ademais, eu concordo com as seguintes condições:
            </Text>
          </View>

          <Text style={{
            color: colors.strongGray, fontSize: 12, paddingTop: 10, textAlign: 'justify'
          }}
          >
            • Se o equipamento for danificado ou inutilizado por emprego inadequado,
            mau uso, negligência ou extravio, a empresa me fornecerá novo equipamento
            e cobrará o valor de mercado de um equipamento da mesma marca ou equivalente
            ao da praça
          </Text>
          <Text style={{
            color: colors.strongGray, fontSize: 12, marginTop: 0, textAlign: 'justify'
          }}
          >
            • Em caso de dano, inutilização ou extravio do
            equipamento deverei comunicar imediatamente ao setor competente.
          </Text>
          <Text style={{
            color: colors.strongGray, fontSize: 12, marginTop: 0, textAlign: 'justify'
          }}
          >
            • Terminando os serviços ou no caso de rescisão do contrato de serviço,
            devolverei o equipamento completo e em perfeito estado de conservação,
            considerando-se o tempo do uso dele ao setor competente
          </Text>
          <Text style={{
            color: colors.strongGray, fontSize: 12, marginTop: 0, textAlign: 'justify'
          }}
          >
            • Estando os equipamentos em minha posse, estarei sujeito a inspeções sem prévio aviso.
          </Text>
          <Text style={{
            color: colors.strongGray, fontSize: 12, paddingTop: 10, textAlign: 'justify'
          }}
          >
            Posto isto, comprometo-me a substituí-lo em caso de perda ou estrago
            irreparável por idêntico ou similar.
          </Text>
          <DoubleTextWithCard
            title="Equipamento"
            text={equipmentName}
            title2="Data limite de devolução"
            text2={formattedLimitDate}
            showSecondCard={isValidDate(formattedLimitDate)}
          />
          <DoubleImage
            title="Visão Geral do Equipamento"
            image={generalImage}
            title2="Identificação do equipamento"
            image2={identificationImage}
          />
          <Signature image={assign} beneficiaryName={beneficiaryName} />
          <Line lineColor={colors.softGray} />
        </View>
      </Page>
    </Document>
  )
}

LoanInformedConsent.propTypes = {
  termData: PropTypes.shape({
    beneficiaryName: PropTypes.string,
    cpf: PropTypes.string,
    generalImage: PropTypes.string,
    identificationImage: PropTypes.string,
    loanDate: PropTypes.string,
    loanLimitDate: PropTypes.string,
    assign: PropTypes.string,
    equipmentName: PropTypes.string,
  })
}
LoanInformedConsent.defaultProps = {
  termData: {
    beneficiaryName: '',
    cpf: '',
    generalImage: '',
    identificationImage: '',
    loanDate: '',
    loanLimitDate: '',
    assign: '',
    equipmentName: '',
  }
}

export default LoanInformedConsent
